import type { CountryMetadata } from "../data/countries";

// Picks N plausible distractors for a multiple-choice prompt. Prefers
// countries from the same region with similar population (the spec calls
// for "similar size or shape"); we approximate "size" with population —
// cheaper than computing polygon area and usually a good proxy for visual
// recognizability ("how often does this country come up?").
//
// Pure: caller passes the rng so tests can be deterministic.

export interface DistractorInput {
  target: CountryMetadata;
  // The pool the distractors are drawn from. Typically the target's whole
  // region; for Mixed mode pass all cleared-region countries.
  pool: CountryMetadata[];
  count: number;
  rng: () => number;
}

const shuffle = <T>(arr: T[], rng: () => number): T[] => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
};

export const pickDistractors = ({
  target,
  pool,
  count,
  rng,
}: DistractorInput): CountryMetadata[] => {
  const candidates = pool.filter((c) => c.iso !== target.iso);
  if (candidates.length <= count) {
    return shuffle(candidates, rng);
  }
  // Rank by population proximity (log scale — small countries cluster on
  // the low end and we want spread). Then take the top N*2 and shuffle to
  // get some variety on repeat plays.
  const targetPop = target.population ?? 1_000_000;
  const targetLog = Math.log10(Math.max(targetPop, 1));
  const scored = candidates.map((c) => {
    const pop = c.population ?? 1_000_000;
    const logDiff = Math.abs(Math.log10(Math.max(pop, 1)) - targetLog);
    return { c, score: logDiff };
  });
  scored.sort((a, b) => a.score - b.score);
  const widePool = scored.slice(0, Math.min(scored.length, count * 3)).map((s) => s.c);
  return shuffle(widePool, rng).slice(0, count);
};
